import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrganizationsService } from 'src/app/domain/organization/components/service/organizations.service';
import { HorNavbarService } from 'src/app/core/service/hor-navbar.service';
import { FollowOnPortfolioDTO } from '../dto/FollowOnPortfolioDTO';
import { Organization } from 'src/app/domain/organization/dto/Organization';
import { CommitmentAllocationModelBackendService } from '../../service/commitment-allocation-model-backend.service';
import { FollowOnModelDTO, NEW_MODEL_NAME } from '../dto/FollowOnModelDTO';
import { FollowOnModelsWithAllocation } from '../dto/FollowOnModelsWithAllocation';
import { CommitmentAllocationModelTableComponent } from '../commitment-allocation-model-table/commitment-allocation-model-table.component';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { ModelButtons } from 'src/app/domain/follow-on/utils/ModelButtons';
import { AuthService } from 'src/app/domain/auth/service/auth.service';
import { FollowOnRoundDTO } from '../dto/FollowOnRoundDTO';
import { DatePipe } from '@angular/common';
import { CanDeactivateComponent } from 'src/app/domain/shared/component/can-deactivate-guard/can-deactivate-guard-interface';
import { UnsavedChangesModalService } from 'src/app/domain/shared/component/can-deactivate-guard/unsaved-changes-modal.service';
import {
  FollowOnInvestmentCalculationsService,
} from '../../service/follow-on-investment-calculations/follow-on-investment-calculations.service';
import { cloneDeep, isEqual } from 'lodash';
import {
  CommitmentAllocationModelAllocation,
  CommitmentAllocModelInfo, ModelDataStatus,
} from '../../utils/follow-on-utils';
import { ModelForTableDTO } from '../dto/ModelForTableDTO';
import { DateService } from '../../../../util/common/date-service/date.service';
import { ValueUtils } from "../../../../core/service/value-utils/value-utils.service";
import { FundPerformanceBackendService } from "../../../fund-performance/service/fund-performance-backend.service";

@Component({
  selector: 'app-commitment-allocation-model',
  templateUrl: './commitment-allocation-model.component.html',
  styleUrls: ['./commitment-allocation-model.component.scss'],
})
export class CommitmentAllocationModelComponent implements OnInit, OnDestroy, CanDeactivateComponent {
  private _destroyStream: Subject<void> = new Subject<void>();
  selectedPeriod: Date;
  periods: Date[];
  selectedOrganization: Organization;
  allocation = new CommitmentAllocationModelAllocation();
  modelInfo = new CommitmentAllocModelInfo();
  modelDataStatus = new ModelDataStatus();
  tabNames: TabCommitmentAllocModel[] = Object.values(TabCommitmentAllocModel);
  selectedTab: TabCommitmentAllocModel = TabCommitmentAllocModel.STEP_1;

  isAdmin: boolean;
  name: string;
  errorMessage: string;
  allowSave = true;
  isEditModel = false;
  show = new ShowCommitmentAllocModel();
  labelsText = LABELS_TEXT;
  deleteModalTextList: string[];
  selectorButtons: ModelButtons = {
    edit: true,
  };

  modalButtons = {
    close: true,
    dismiss: 'No',
    apply: 'Yes',
  };

  @ViewChild('commitmentAllocTable') commitmentAllocTable: CommitmentAllocationModelTableComponent;

  constructor(
    public datePipe: DatePipe,
    private orgService: OrganizationsService,
    private navbarService: HorNavbarService,
    private commitmentAllocService: CommitmentAllocationModelBackendService,
    private notificationsService: NotificationsService,
    private authService: AuthService,
    private unsavedChangesModalService: UnsavedChangesModalService,
    private fpService: FundPerformanceBackendService
  ) {}

  ngOnInit(): void {
    this.authService.currentUserTypeForServices$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        this.isAdmin = userType === "admin";
      });
    this.initOrgSubscription();
    this.initPeriodsSubscription();
  }

  /**
   * Prevent leaving the page when user has unsaved changes
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (this.unsavedChangesModalService.getIsInEditMode()) {
      return this.unsavedChangesModalService.showConfirmDialog();
    }
    return true;
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async onCopyModelClick(): Promise<void> {
    const allocation = this.allocation.selected;
    const selectedModel = cloneDeep(this.modelInfo.selected);
    selectedModel.id = 0;
    selectedModel.uuid = '';
    selectedModel.name = this.setCopyName(this.modelInfo.selected.name);
    this.setEditModelsAndMultiplyExitProbs();
    const modelToSave: FollowOnModelsWithAllocation = {
      newInvestment: this.removeIds(this.modelDataStatus.forEdit.newInvestment.models),
      active: this.modelDataStatus.forEdit.active.models,
      sold: this.modelDataStatus.forEdit.sold.models,
      allocation,
      followOnModelDTO: selectedModel,
    };
    const res = await this.commitmentAllocService.save(modelToSave, this.isAdmin);
    this.selectorButtons = {
      edit: true,
    };
    this.allocation.selected = res?.allocation;
    this.modelDataStatus.initial.newInvestment.models = res?.newInvestment;
    this.modelDataStatus.initial.active.models = res?.active;
    this.modelDataStatus.initial.sold.models = res?.sold;
    this.modelDataStatus.forEdit = cloneDeep(this.modelDataStatus.initial);
    this.countEditableTotals();
    this.modelInfo.selected = res?.followOnModelDTO;
    this.modelInfo.all.push(this.modelInfo.selected);
    this.modelInfo.all.sort((m1, m2) => new Date(m1.lastModifiedDate).getTime() - new Date(m2.lastModifiedDate).getTime());
    this.setLastSaveTest();
    this.name = this.modelInfo.selected.name;
    this.modelInfo.initial = cloneDeep(this.modelInfo.selected);
    this.edit(false);
  }

  setOpEx(opEx: number): void {
    this.allocation.selected.opEx = opEx;
    this.setAllocationLeft();
  }

  setModelInfo(model: FollowOnModelDTO): void {
    this.modelInfo.selected = model;
    this.modelInfo.initial = cloneDeep(this.modelInfo.selected);
    if (model.name === NEW_MODEL_NAME) {
      this.modelInfo.selected = new FollowOnModelDTO(this.selectedPeriod);
      this.name = '';
      this.allocation.selected = this.allocation.initial;
      this.show.emptyStateNoPortfolio = false;
      return;
    }
    this.getModelData(model, model.period);
  }

  async save(): Promise<void> {
    this.checkAllowSave();
    if (!this.allowSave) {
      return;
    }

    let selectedModel = this.modelInfo.selected;
    const isNewModel = !this.modelInfo.selected.name || this.modelInfo.selected?.name === NEW_MODEL_NAME;
    if (isNewModel) {
      selectedModel = {
        id: 0,
        uuid: '',
        period: this.selectedPeriod,
        name: this.name,
        newCompaniesInvestment: this.allocation.selected.newCompaniesInvestment,
        createdBy: '',
      };
    } else {
      selectedModel.name = this.name;
      selectedModel.period = this.selectedPeriod;
    }
    const modelToSave: FollowOnModelsWithAllocation = {
      newInvestment: this.modelDataStatus.forEdit.newInvestment.models,
      active: this.modelDataStatus.forEdit.active.models,
      sold: this.modelDataStatus.forEdit.sold.models,
      allocation: this.allocation.selected,
      followOnModelDTO: selectedModel,
    };

    // check if there are new investments to be deleted
    if (this.commitmentAllocTable.newInvestmentsToBeDeleted.length) {
      await this.commitmentAllocService.deleteNewInvestments(this.commitmentAllocTable.newInvestmentsToBeDeleted,
        this.selectedOrganization.selectedFund.uuid);
      this.commitmentAllocTable.newInvestmentsToBeDeleted = [];
    }
    const res = await this.commitmentAllocService.save(modelToSave, this.isAdmin);
    this.selectorButtons = {
      edit: true,
    };
    this.allocation.selected = this.allocation.initial = res.allocation;
    this.modelDataStatus.initial.newInvestment.models = res.newInvestment;
    this.modelDataStatus.initial.active.models = res.active;
    this.modelDataStatus.initial.sold.models = res.sold;
    this.modelDataStatus.forEdit = cloneDeep(this.modelDataStatus.initial);
    this.modelInfo.all = res.savedModels;

    this.modelInfo.all.sort((m1, m2) => new Date(m2.lastModifiedDate).getTime() - new Date(m1.lastModifiedDate).getTime());
    this.setLastSaveTest();
    this.modelInfo.selected = this.modelInfo.all.find((m) => m.uuid === res.followOnModelDTO.uuid);
    this.modelInfo.initial = cloneDeep(this.modelInfo.selected);
    this.edit(false);
    this.countEditableTotals();
  }

  edit(isEdit: boolean): void {
    this.isEditModel = isEdit;
    this.unsavedChangesModalService.setIsInEditMode(isEdit);
    if (isEdit) {
      this.name = this.modelInfo.selected.name === NEW_MODEL_NAME ? '' : this.modelInfo.selected.name;
      this.setEditModelsAndMultiplyExitProbs();
      this.countEditableTotals();
      return;
    }
    if (this.modelInfo.all?.length && !this.modelInfo.initial?.uuid) {
      this.modelInfo.selected = this.modelInfo.all[0];
      this.setModelInfo(this.modelInfo.selected);
    } else if (this.modelInfo.initial) {
      this.modelInfo.selected = this.modelInfo.initial;
      this.selectedPeriod = this.modelInfo.selected.period;
    }
    this.setAllocationLeft();
  }

  async delete(value: boolean): Promise<void> {
    this.show.modalThinkTwice = false;
    if (!value) {
      return;
    }
    this.modelInfo.all = this.modelInfo.all.filter((model) => model.uuid !== this.modelInfo.selected.uuid);
    if (!this.modelInfo.all.length) {
      this.show.emptyStateNoModels = true;
    }
    this.setLastSaveTest();
    await this.commitmentAllocService.delete(this.modelInfo.selected.uuid);
    this.resetData(false);
    this.modelInfo.selected = this.modelInfo.all.length ? this.modelInfo.all[0] :
      new FollowOnModelDTO(this.periods.length ? this.periods[0] : null);
    this.selectedPeriod = this.modelInfo.selected.period;
    await this.getModelData(this.modelInfo.selected, this.modelInfo.selected.period);
  }

  setSelectedTab(tab: TabCommitmentAllocModel): void {
    this.selectedTab = tab;
  }

  async setPeriod(date: Date, userTypeChanged: boolean): Promise<void> {
    if (date !== this.selectedPeriod || userTypeChanged) {
      this.selectedPeriod = date;
      await this.getModelData(this.modelInfo.selected, date);
    }
  }

  countFollowOnAllocation(): void {
    this.allocation.selected.followOnAllocation = FollowOnInvestmentCalculationsService
      .countFollowOnAllocation(this.modelDataStatus.forEdit.active.models);
    this.countEditableTotals();
    this.setAllocationLeft();
  }

  countTotal(data: ModelForTableDTO, isSold = false): void {
    if (!data) {
      return;
    }
    data.total = FollowOnInvestmentCalculationsService.countTotal(data.models, isSold);
  }

  countEditableTotals(): void {
    this.countTotal(this.modelDataStatus.forEdit.newInvestment);
    this.countTotal(this.modelDataStatus.forEdit.active);
    this.allocation.selected.newCompaniesInvestment = this.modelDataStatus.forEdit.newInvestment.total.currentCost +
      FollowOnInvestmentCalculationsService.countFollowOnAllocation(this.modelDataStatus.forEdit.newInvestment.models);
    this.setAllocationLeft();
  }

  async onModelNameEmit(modelName: string): Promise<void> {
    if (!modelName) {
      return;
    }
    await this.onNewModelClick();
    this.modelInfo.selected.name = this.name = modelName;
    this.show.emptyStateNoModels = false;
  }

  async buttonClicked(value: string): Promise<void> {
    switch (value) {
      case 'new': {
        await this.onNewModelClick();
        break;
      }
      case 'edit': {
        this.onEditModelClick();
        break;
      }
      case 'apply': {
        await this.save();
        break;
      }
      case 'copy': {
        await this.onCopyModelClick();
        break;
      }
      case 'delete': {
        this.show.modalThinkTwice = true;
        this.deleteModalTextList = ['Model ' + this.modelInfo.selected.name + ' will be deleted'];
        break;
      }
      case 'cancel': {
        this.show.unsavedChangesModal = true;
        break;
      }
    }
  }

  closeCancelModal(close: boolean): void {
    if (close) {
      this.modelInfo.selected = cloneDeep(this.modelInfo.initial);
      this.modelDataStatus.forEdit = cloneDeep(this.modelDataStatus.initial);
      this.countTotal(this.modelDataStatus.forEdit.sold, true);
      this.countEditableTotals();
      this.edit(false);
      this.selectorButtons = {
        edit: true,
      };
    }
    this.show.unsavedChangesModal = false;
    this.show.emptyStateNoPortfolio =
      !(this.modelDataStatus.forEdit.active?.models.length || this.modelDataStatus.forEdit.sold?.models.length);
    this.show.emptyStateNoModels = this.modelInfo.all.length === 0;
  }

  addNewInvestment(): void {
    this.modelDataStatus.forEdit.newInvestment.models.push(new FollowOnPortfolioDTO());
    // trigger appSort change detection
    this.modelDataStatus.forEdit.newInvestment.models = [...this.modelDataStatus.forEdit.newInvestment.models];
    this.countEditableTotals();
  }

  static getValidationMessage(model: FollowOnPortfolioDTO): string {
    if (!model.name) {
      return NOTIFICATIONS.COMPANY_NAME_EMPTY.message;
    }

    const r1 = model.rounds[0];
    const r2 = model.rounds[1];
    const r3 = model.rounds[2];

    if (ValueUtils.isValuePresent(r1.exitValue) && r1.exitProbability && ValueUtils.isValueAbsent(r2.exitValue) && Number(r1.exitProbability) !== 100) {
      return NOTIFICATIONS.ONE_ROUND_EXIT_VALUE_NOT_100.message;
    }

    if (r1.exitProbability && (ValueUtils.isValuePresent(r2.exitValue)) && r2.exitProbability && ValueUtils.isValueAbsent(r3.exitValue) &&
      Number(r1.exitProbability) + Number(r2.exitProbability) !== 100) {
      return NOTIFICATIONS.TWO_ROUNDS_EXIT_VALUE_NOT_100.message;
    }

    if (!this.areExitProbsPresentAndValid(r1, r2, r3)) {
      return NOTIFICATIONS.THREE_ROUNDS_EXIT_VALUE_NOT_100.message;
    }

    return null; // no errors
  }

  static areExitProbsPresentAndValid(r1: FollowOnRoundDTO, r2: FollowOnRoundDTO, r3: FollowOnRoundDTO): boolean {
    const areAllProbabilitiesPresent = ValueUtils.isValuePresent(r1.exitProbability)
      && ValueUtils.isValuePresent(r2.exitProbability)
      && ValueUtils.isValuePresent(r3.exitProbability);
    if (areAllProbabilitiesPresent) {
      const totalProbability = r1.exitProbability + r2.exitProbability + r3.exitProbability;
      return totalProbability === 100;
    }
    return true;
  }

  private async getSavedModels(): Promise<void> {
    const period = this.selectedPeriod || this.periods?.[0] || null;
    if (!period) {
      this.show.emptyStateNoPortfolio = true;
      return;
    }
    // have to do a manual isAdmin check because user type sub may get its value after the periods sub triggers getSavedModels
    const isAdmin = this.authService.getCurrentUserType() === "admin";
    const allModels = await this.commitmentAllocService.getSavedModels(this.selectedOrganization.selectedFund.uuid, isAdmin);
    if (isEqual(this.modelInfo.all, allModels)) {
      return; // prevent unnecessary page refresh
    }
    this.modelInfo.all = allModels;
    this.modelInfo.selected = this.modelInfo.all?.length ? this.modelInfo.all[0] : new FollowOnModelDTO(this.periods[0]);
    await this.getInitialData(this.modelInfo.selected, this.modelInfo.selected.period);
    if (!this.modelDataStatus.initial.active.models.length && !this.modelDataStatus.initial.sold.models.length) {
      this.show.emptyStateNoPortfolio = true;
      return;
    }
    this.show.emptyStateNoPortfolio = false;
    this.show.emptyStateNoModels = !this.modelInfo.all?.length;
    this.setLastSaveTest();
    await this.setPeriod(this.modelInfo.selected.period, true);
  }

  private setAllocationLeft(): void {
    this.allocation.selected.allocationLeft = this.allocation.selected?.totalCommitment -
      this.allocation.selected.totalCost - Math.round(this.allocation.selected.opEx * this.allocation.selected.totalCommitment) -
      this.allocation.selected.newCompaniesInvestment - this.allocation.selected.followOnAllocation;
  }

  private async getInitialData(selectedModel: FollowOnModelDTO, period: Date): Promise<void> {
    await this.getModelData(selectedModel, period);
    this.allocation.initial = this.allocation.selected;
  }

  private async getModelData(selectedModel: FollowOnModelDTO, period: Date): Promise<void> {
    const res = await this.commitmentAllocService.getData(
      this.selectedOrganization.selectedFund.uuid,
      period,
      selectedModel.uuid ?? '',
    );
    if ((!res?.active?.length && !res?.sold?.length) || !res?.allocation || !period) {
      this.show.emptyStateNoPortfolio = true;
      return;
    }
    this.show.emptyStateNoPortfolio = false;
    this.selectedPeriod = period;
    this.allocation.selected = res.allocation;
    this.modelDataStatus.initial.newInvestment = this.initNewModelData(res.newInvestment);
    this.modelDataStatus.initial.active = this.initNewModelData(res.active);
    this.modelDataStatus.initial.sold = this.initNewModelData(res.sold, true);
    if (this.isEditModel) {
      this.setEditModelsAndMultiplyExitProbs();
      return;
    }
    this.modelDataStatus.forEdit = cloneDeep(this.modelDataStatus.initial);
    this.allocation.selected.newCompaniesInvestment = this.modelDataStatus.forEdit.newInvestment.total.currentCost;
    this.setAllocationLeft();
    if (this.commitmentAllocTable?.newInvestmentsToBeDeleted?.length) {
      this.commitmentAllocTable.newInvestmentsToBeDeleted = [];
    }
  }

  private initNewModelData(models: FollowOnPortfolioDTO[], isSold = false): ModelForTableDTO {
    const modelData = new ModelForTableDTO();
    modelData.models = models;
    modelData.total = FollowOnInvestmentCalculationsService.countTotal(models, isSold);
    return modelData;
  }

  private resetData(withInitials: boolean): void {
    this.selectedPeriod = undefined;
    this.modelInfo.selected = undefined;
    this.allocation.selected = undefined;
    this.modelDataStatus = new ModelDataStatus();
    if (withInitials) {
      this.allocation = new CommitmentAllocationModelAllocation();
    }
    this.selectorButtons = {
      edit: true,
    };
  }

  private checkAllowSave(): void {
    this.allowSave = this.validateModelName();
    if (!this.allowSave) {
      return;
    }
    this.allowSave = this.validateEditableModels();
  }

  private validateModelName(): boolean {
    if (this.modelInfo.selected.name === this.name) {
      return true;
    }
    const isNameDuplicate = this.modelInfo.all.some((model) => model.name.toLowerCase() === this.name?.toLowerCase());
    const isNamePresent = Boolean(this.name);
    this.errorMessage = isNameDuplicate ? 'Model with this name already exists' : undefined;
    const isNameValid = isNamePresent && !isNameDuplicate;

    if (!isNameValid) {
      this.notificationsService.warning(isNameDuplicate ? NOTIFICATIONS.MODEL_NAME_ALREADY_EXISTS : NOTIFICATIONS.MODEL_NAME_EMPTY);
    }

    return isNameValid;
  }

  private validateEditableModels(): boolean {
    const editableModels = [
      ...this.modelDataStatus.forEdit.active.models,
      ...this.modelDataStatus.forEdit.newInvestment.models,
    ];

    for (const model of editableModels) {
      const validationMessage = CommitmentAllocationModelComponent.getValidationMessage(model);
      if (validationMessage) {
        this.notificationsService.warning({
          title: "Warning",
          message: validationMessage,
        });
        return false;
      }
    }

    return true;
  }

  private setLastSaveTest(): void {
    this.modelInfo.all.forEach((model) => {
      if (model.name !== NEW_MODEL_NAME) {
        model.lastSaveText = (model.isFirstSave ? 'Created' : 'Updated')
          + (model.createdBy ? ' by ' + model.createdBy : '') + ': '
          + this.datePipe.transform(model.lastModifiedDate, 'dd-MM-yyyy');
      }
    });
  }

  private initOrgSubscription(): void {
    this.orgService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org): void => {
        this.resetData(true);
        if (!org) {
          return;
        }
        this.selectedOrganization = org;
        this.isEditModel = false;
        this.selectedTab = TabCommitmentAllocModel.STEP_1;
      });
  }

  /**
   * Initializes the subscription to handle periods based on the {@link HorNavbarService}'s updates.
   *
   * ### Functionality:
   * - **Viewers**: Can only see visible periods (i.e., periods where the corresponding {@link FundPerformance} is marked as visible
   * for the selected fund).
   * - **Admins**: Can see all models, including those with invisible periods, but they cannot assign an invisible period to a model.
   *
   * ### Trigger:
   * - The navbar subscription acts as the trigger for fetching visible periods from the backend.
   * - When periods are updated in the navbar service, the component responds by filtering visible periods.
   *
   * ### Flow:
   * 1. Subscribe to the `getPeriods` observable from the navbar service.
   * 2. When periods change:
   *    - If periods are `null`, clear the current periods.
   *    - Fetch and sort the visible periods from the backend.
   *    - If the visible periods differ from the current periods, update them.
   * 3. Call `getSavedModels()` to fetch the models if periods differ.
   */
  private initPeriodsSubscription(): void {
    this.navbarService.getPeriods
      .pipe(takeUntil(this._destroyStream))
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async (periods) => {
        if (!periods) {
          this.periods = periods;
          return;
        }
        const visiblePeriods = await this.getOnlyVisiblePeriods();
        visiblePeriods.sort((el1, el2) =>
          DateService.compareTwoDates(el2.toString(), el1.toString())
        );
        if (periods.length > 0 && visiblePeriods.length === 0) {
          this.notificationsService.warning(NOTIFICATIONS.NO_VISIBLE_PERIODS);
        }
        this.periods = visiblePeriods;
        await this.getSavedModels();
      });
  }

  private async onNewModelClick(): Promise<void> {
    this.modelInfo.initial = cloneDeep(this.modelInfo.selected);
    this.modelInfo.selected = new FollowOnModelDTO(this.periods[0]);
    this.selectedPeriod = this.periods[0];

    const res = await this.commitmentAllocService.getData(
      this.selectedOrganization.selectedFund.uuid,
      this.selectedPeriod,
      '',
    );
    if ((!res.active?.length && !res?.sold?.length) || !res?.allocation) {
      this.show.emptyStateNoPortfolio = true;
      return;
    }
    this.show.emptyStateNoPortfolio = false;
    this.allocation.selected = res.allocation;
    this.modelDataStatus.forEdit.newInvestment = new ModelForTableDTO();
    this.modelDataStatus.forEdit.active = this.initNewModelData(res.active);
    this.modelDataStatus.forEdit.sold = this.initNewModelData(res.sold, true);
    this.allocation.selected.newCompaniesInvestment = 0;
    this.setAllocationLeft();
    this.isEditModel = true;
    this.unsavedChangesModalService.setIsInEditMode(true);
    this.name = "";
    this.selectorButtons = {
      apply: true,
      cancel: true,
    };
  }

  private onEditModelClick(): void {
    this.modelInfo.initial = cloneDeep(this.modelInfo.selected);
    this.edit(true);
    this.selectorButtons = {
      apply: true,
      copy: true,
      delete: true,
      cancel: true,
    };
  }

  private setEditModelsAndMultiplyExitProbs(): void {
    this.modelDataStatus.forEdit = cloneDeep(this.modelDataStatus.initial);

    // from tsar goroh times exitProbability values are treated like [0-100] but stored on backend as [0-1].
    // since frontend logic treats them as [0-100], we transform them manually here

    this.modelDataStatus.forEdit.active.models.forEach((model) => {
      model.rounds.forEach((round) => {
        if (round.exitProbability && round.exitProbability <= 1) {
          round.exitProbability = round.exitProbability * 100;
        }
      });
    });
    this.modelDataStatus.forEdit.newInvestment.models.forEach((model) => {
      model.rounds.forEach((round) => {
        if (round.exitProbability && round.exitProbability <= 1) {
          round.exitProbability = round.exitProbability * 100;
        }
      });
    });
  }

  private removeIds(models: FollowOnPortfolioDTO[]): FollowOnPortfolioDTO[] {
    const copy = cloneDeep(models);
    copy.forEach((model) => {
      model.uuid = undefined;
      model.rounds.forEach((round) => (round.uuid = undefined));
    });
    return copy;
  }

  private setCopyName(selectedModelName: string): string {
    let copyName: string;

    // Check if the selected model name matches the pattern for a copy
    const match = selectedModelName.match(COPY_REGEX);
    if (match) {
      // If the name already has a "copy" suffix, extract the base name and current copy number
      const baseName = match[1];
      const copyNumber = match[3] ? parseInt(match[3], 10) : 0;

      // Generate the new name with an incremented copy number
      copyName = copyNumber > 0 ? `${baseName} copy (${copyNumber + 1})` : `${baseName} copy (1)`;
    } else {
      // If the name doesn't have a "copy" suffix, simply add "copy"
      copyName = `${selectedModelName} copy`;
    }

    // Ensure the new name is unique by checking against existing names and incrementing the number if necessary
    while (this.modelInfo.all.some((model) => model.name.toLowerCase() === copyName.toLowerCase())) {
      const matchNew = copyName.match(COPY_REGEX);
      const baseNameNew = matchNew[1];
      const copyNumberNew = matchNew[3] ? parseInt(matchNew[3], 10) : 0;
      copyName =
        copyNumberNew > 0
          ? `${baseNameNew} copy (${copyNumberNew + 1})`
          : `${baseNameNew} copy (1)`;
    }

    return copyName;
  }

  private async getOnlyVisiblePeriods(): Promise<Date[]> {
    return firstValueFrom(this.fpService.getAllVisiblePeriods(this.selectedOrganization.selectedFund.uuid));
  }
}

export enum TabCommitmentAllocModel {
  STEP_1 = 'Step 1. Growth prediction',
  STEP_2 = 'Step 2. Investment allocation',
  STEP_3 = 'Step 3. Performance prediction',
}

const LABELS_TEXT = {
  info: 'is built to predict potential multiple on the next investment round based on' +
    ' portfolio future growth assumptions helping to allocate commitment in a more efficient way.',
  disclaimer: 'this simulation model\'s results are solely for reference and should not be viewed as absolute predictions' +
    ' or assurances. User bear complete accountability for understanding and using these outcomes.',
};

export class ShowCommitmentAllocModel {
  modalThinkTwice = false;
  emptyStateNoPortfolio = false;
  emptyStateNoModels = false;
  unsavedChangesModal = false;
}

// regex to match and capture parts of the name with "copy" suffix
const COPY_REGEX = /(.*?)( copy(?: \((\d+)\))?)$/i;
